// Copyright (c) 2026 Celadon Development LLC, All rights reserved.
// Author Vasily Klyuev <vasily.klyuev@celadon.ae>
// The rules for "how much is being taken off this voucher", in one place.
//
// Shared by both redemption screens - the standalone Redeem voucher page and
// the box on the voucher-confirmed page - and by their sagas. They used to
// disagree: one accepted 2, 2.7 and 2.76, the other demanded exactly two
// decimals and rejected "0.5" outright, which is not something a shop should
// have to know per screen.
//
// The page needs to know whether an amount is sane before asking "are you
// sure" - there is no point confirming "take £NaN off this voucher". The saga
// needs the same rules because it is the last gate before the request. One
// copy, so the two cannot drift apart and start disagreeing about what counts
// as a valid amount.

/**
 * Up to two decimals, so 2, 2.7 and 2.76 are all accepted - the old screen
 * demanded exactly two and silently rejected "2.7".
 */
export const AMOUNT_PATTERN = /^[0-9]+([.][0-9]{1,2})?$/;

/** Whitespace off both ends: scanners and pasted values bring it along. */
export const cleanAmount = amount => String(amount == null ? '' : amount).trim();

/**
 * True when the amount is a positive figure of the right shape AND fits on the
 * voucher. Balance is checked here for a quick answer and again by the server,
 * which is the authority - what is on screen may be seconds stale.
 */
export const isRedeemableAmount = (amount, balance) => {
  const cleaned = cleanAmount(amount);

  if (!AMOUNT_PATTERN.test(cleaned) || parseFloat(cleaned) <= 0) {
    return false;
  }

  return parseFloat(cleaned) <= parseFloat(balance);
};

/**
 * The reason an amount cannot be redeemed, or null when it can.
 *
 * Returning the message rather than a boolean keeps the wording identical on
 * both screens and in both sagas.
 */
export const redeemAmountError = (amount, balance) => {
  const cleaned = cleanAmount(amount);

  if (!cleaned) {
    return 'Enter an amount, for example 2.76.';
  }

  if (!AMOUNT_PATTERN.test(cleaned) || parseFloat(cleaned) <= 0) {
    return 'Enter an amount, for example 2.76.';
  }

  if (parseFloat(cleaned) > parseFloat(balance)) {
    return `That is more than the £${parseFloat(balance).toFixed(2)} left on this voucher.`;
  }

  return null;
};
